import type { Persisted, Profile } from '@tunasong/schemas'
import type { FC, MouseEvent } from 'react'
import styles from './collaborator-row.module.css'

export interface CollaboratorRowProps {
  profile: Persisted<Profile>
  /** DOM id, so a combobox can point at the highlighted row with aria-activedescendant */
  id?: string
  highlighted?: boolean
  onClick?(): void
}

const initials = (profile: Persisted<Profile>): string => {
  const source = profile.name ?? profile.email ?? ''
  const parts = source.split(/\s+|@/).filter(Boolean)
  if (parts.length === 0) {
    return '?'
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2)
  }
  return parts[0][0] + parts[1][0]
}

export const CollaboratorRow: FC<CollaboratorRowProps> = props => {
  const { profile, id, highlighted = false, onClick } = props
  const handleClick = (ev: MouseEvent) => {
    ev.preventDefault()
    onClick?.()
  }
  const hasName = Boolean(profile.name)
  return (
    <div
      id={id}
      className={styles.row}
      data-highlighted={highlighted ? 'true' : undefined}
      onClick={handleClick}
      role="option"
      aria-selected={highlighted}
    >
      <span className={styles.avatar} aria-label={profile.name ?? profile.email ?? 'Unnamed user'}>
        {initials(profile)}
      </span>
      <span className={styles.text}>
        {hasName ? <span className={styles.name}>{profile.name}</span> : null}
        {/* Other users' profiles carry no email */}
        {profile.email ? <span className={styles.email}>{profile.email}</span> : null}
        {!hasName && !profile.email ? <span className={styles.name}>Unnamed user</span> : null}
      </span>
    </div>
  )
}
